import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { MenuCacheService } from '../../features/table-connect/menu/menu-cache.service';

/** GTM container id shape — guards a stray value before we hit the network. */
const GTM_CONTAINER_RE = /^GTM-[A-Z0-9]+$/i;

/** `window` augmented with the GTM data layer we push virtual page-views to. */
type DataLayerWindow = typeof window & { dataLayer?: unknown[] };

/**
 * TC-GTAG — runtime injector for the TableConnect guest-surface Google Tag
 * Manager container.
 *
 * **What it does.** When `bindToken(token)` is called, this service
 * subscribes to {@link MenuCacheService.token$} and, on the first successful
 * resolve that carries a non-null `gtmContainerId`, bootstraps GTM exactly
 * once (data layer + async `gtm.js`). The container id is resolved entirely
 * server-side — see docs/new/tableconnect-google-tag-trial-capture.md — so
 * this service never decides *whether* to track, only *renders* whatever the
 * resolve hands it:
 *   - the store's own container (paid tenants), or
 *   - the platform capture container (not-yet-paid tenants, first 14 days), or
 *   - nothing (null → no tag injected).
 *
 * **One container per SPA load.** A guest never spans two stores in one tab,
 * and GTM cannot hot-swap a container id without a reload, so the first
 * resolved id wins and later changes are ignored until the next page load.
 *
 * **SPA page-views.** TableConnect is a client-routed SPA, so a single
 * document load would otherwise look like one page-view. After the container
 * loads we push a `tc_virtual_page_view` data-layer event for the current URL
 * and on every subsequent `NavigationEnd`, giving the container a reliable
 * per-screen trigger without relying on GTM's History-Change auto-listener.
 *
 * **No-op guards.** Bails cleanly when `document`/`window` are absent
 * (SSR / unit tests) and when the id fails the `GTM-` shape check.
 */
@Injectable({ providedIn: 'root' })
export class GoogleTagService implements OnDestroy {
  private menuCache = inject(MenuCacheService);
  private router = inject(Router);
  private destroy$ = new Subject<void>();

  /** Tokens we've subscribed for. Idempotent bind (mirrors TcThemeService). */
  private boundTokens = new Set<string>();

  /** The GTM container injected this SPA load, or null until one loads. */
  private loadedContainerId: string | null = null;

  /**
   * Subscribe (idempotently) to a token's resolve feed and inject the
   * resolved GTM container, if any. Call once per token from the app's
   * navigation handler — same entry-point as {@link TcThemeService.bindToken}.
   */
  bindToken(token: string): void {
    if (!token || this.boundTokens.has(token)) return;
    this.boundTokens.add(token);

    // Trigger the resolve without pulling the full menu (categories/items);
    // a menu-data page kicks off the full `bind()` from its own ngOnInit.
    this.menuCache.bindTokenOnly(token);

    this.menuCache.token$(token)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result.status !== 'ok') return;
        const containerId = result.data.gtmContainerId ?? null;
        if (containerId) this.ensureContainer(containerId);
      });
  }

  /** Inject `gtm.js` for `containerId` exactly once per SPA load. */
  private ensureContainer(containerId: string): void {
    if (this.loadedContainerId) return;
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    if (!GTM_CONTAINER_RE.test(containerId)) return;

    // Defensive: if a tag was somehow already injected (HMR / double bind),
    // adopt it rather than adding a second <script>.
    if (document.getElementById('tc-gtm')) {
      this.loadedContainerId = containerId;
      this.startVirtualPageViews();
      return;
    }

    this.loadedContainerId = containerId;

    const w = window as DataLayerWindow;
    w.dataLayer = w.dataLayer ?? [];
    w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.id = 'tc-gtm';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
    document.head.appendChild(script);

    this.startVirtualPageViews();
  }

  /** Push the current URL, then one event per subsequent navigation. */
  private startVirtualPageViews(): void {
    this.pushVirtualPageView(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(e => this.pushVirtualPageView(e.urlAfterRedirects));
  }

  private pushVirtualPageView(url: string): void {
    const w = window as DataLayerWindow;
    if (!w.dataLayer) return;
    w.dataLayer.push({ event: 'tc_virtual_page_view', page_path: url });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
